// Genel sekmesi — rut planı form kartı + "Rut Temsilcileri" kartı (temsilci atama
// eski ayrı sekmeden buraya taşındı). Form alanları üst bileşende tutulur (form/set).
import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { RELATION_CONFIGS } from '../relationConfigs';
import { MultiChipSelect } from '../campaign/MultiChipSelect';
import { API_BASE, pick, unwrap, STATUS_OPTIONS, fmtDate } from './routePlanUtils';

export default function GeneralTab({ form, set, isEdit, isNew, planId, showToast }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760 }}>
      <RoutePlanForm form={form} set={set} isEdit={isEdit} />
      {!isNew && <RepresentativesCard planId={planId} isEdit={isEdit} showToast={showToast} />}
    </div>
  );
}

function RoutePlanForm({ form, set, isEdit }) {
  return (
    <div style={card}>
      <div style={fieldWrap}>
        <label style={labelStyle}>Rut Adı *</label>
        <input style={s.input} value={form.name} disabled={!isEdit} onChange={(e) => set({ name: e.target.value })} placeholder="Örn. İstanbul Avrupa Yakası Haftalık" />
      </div>
      <div style={fieldWrap}>
        <label style={labelStyle}>Kod (opsiyonel)</label>
        <input style={s.input} value={form.textId} disabled={!isEdit} onChange={(e) => set({ textId: e.target.value })} placeholder="Örn. RUT-IST-01" />
      </div>
      {/* #800 ÇOKLU DİSTRİBÜTÖR. Alan eskiden tek seçimlik RelationPicker'dı ve zorunluydu; bir plan
          birden çok distribütörü kapsayamıyor, "hepsi" de ifade edilemiyordu. Artık çoklu çip
          seçimi ve BOŞ BIRAKMAK geçerli bir seçim: kapsam yok = tüm distribütörler. */}
      <div style={fieldWrap}>
        <label style={labelStyle}>Distribütörler</label>
        <MultiChipSelect
          items={form.vendors || []}
          onChange={(items) => set({ vendors: items })}
          config={RELATION_CONFIGS.vendor}
          placeholder="Distribütör ara ve ekle…"
          disabled={!isEdit}
        />
        <p style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)', margin: '6px 0 0' }}>
          {(form.vendors || []).length === 0
            ? 'Seçim yapılmadı → plan TÜM distribütörleri kapsar; müşteri havuzu daraltılmaz.'
            : `${form.vendors.length} distribütör seçili — müşteri ve temsilci adayları bunlarla sınırlanır.`}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ ...fieldWrap, flex: 1 }}>
          <label style={labelStyle}>Başlangıç *</label>
          <input style={s.input} type="date" value={form.startDate} disabled={!isEdit} onChange={(e) => set({ startDate: e.target.value })} />
        </div>
        <div style={{ ...fieldWrap, flex: 1 }}>
          <label style={labelStyle}>Bitiş *</label>
          <input style={s.input} type="date" value={form.endDate} disabled={!isEdit} onChange={(e) => set({ endDate: e.target.value })} />
        </div>
      </div>
      <div style={{ ...fieldWrap, marginBottom: 0 }}>
        <label style={labelStyle}>Durum</label>
        <select style={s.input} value={form.status} disabled={!isEdit} onChange={(e) => set({ status: e.target.value })}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {form.createdDate && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)', marginTop: 12, marginBottom: 0 }}>
          Oluşturma: {fmtDate(form.createdDate)} · {form.createdBy || '—'}
        </p>
      )}
    </div>
  );
}

// ── Rut Temsilcileri kartı ──
function RepresentativesCard({ planId, isEdit, showToast }) {
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/${planId}/representatives`, { headers: authHeaders() }),
        fetch(`${API_BASE}/${planId}/representative-candidates`, { headers: authHeaders() }),
      ]);
      if (!aRes.ok) throw new Error(`Temsilciler alınamadı (${aRes.status})`);
      setRows(unwrap(await aRes.json()) || []);
      setCandidates(cRes.ok ? (unwrap(await cRes.json()) || []) : []);
    } catch (e) {
      showToast?.('error', e.message);
    }
  }, [planId, showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const searchCandidates = async (query) => {
    const term = String(query || '').toLocaleLowerCase('tr-TR');
    return candidates
      .map((c) => ({ value: pick(c, 'id', 'Id'), label: pick(c, 'name', 'Name') ?? pick(c, 'textId', 'TextId') ?? '' }))
      .filter((o) => !term || o.label.toLocaleLowerCase('tr-TR').includes(term))
      .slice(0, 50);
  };

  const addReps = async () => {
    if (picks.length === 0) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${planId}/representatives`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ representativeIds: picks.map((p) => p.value) }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || 'Atama başarısız');
      showToast?.('success', pick(payload, 'Message', 'message') || 'Temsilciler atandı.');
      setPicks([]); setAdding(false);
      fetchAll();
    } catch (e) { showToast?.('error', e.message); } finally { setBusy(false); }
  };

  const removeRep = async (rprId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${planId}/representatives/${rprId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Kaldırma başarısız');
      showToast?.('success', 'Temsilci ataması kaldırıldı.');
      fetchAll();
    } catch (e) { showToast?.('error', e.message); } finally { setBusy(false); }
  };

  const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toLocaleUpperCase('tr-TR');

  return (
    <div style={repCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={repIcon}>👥</div>
        <div>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#fff' }}>Rut Temsilcileri</h4>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.78)' }}>Bu planı yürütecek temsilciler.</p>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '3px 10px' }}>
          {rows.length} kişi
        </span>
        {isEdit && (
          <button style={repAddBtn} onClick={() => setAdding((v) => !v)}>{adding ? 'Kapat' : '+ Temsilci Ekle'}</button>
        )}
      </div>

      {isEdit && adding && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 14, background: 'rgba(255,255,255,0.95)', padding: 12, borderRadius: 12 }}>
          <div style={{ flex: 1 }}>
            <MultiChipSelect items={picks} onChange={setPicks} fetchOptions={searchCandidates} placeholder="Temsilci ara ve ekle (aynı distribütör)…" />
          </div>
          <button style={{ ...s.btnPrimary, opacity: (busy || picks.length === 0) ? 0.6 : 1 }} onClick={addReps} disabled={busy || picks.length === 0}>Ata</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.length === 0 ? (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', margin: 0 }}>Henüz temsilci atanmadı.</p>
        ) : rows.map((r) => {
          const id = pick(r, 'id', 'Id');
          const name = pick(r, 'representativeName', 'RepresentativeName') || '—';
          return (
            <div key={id} style={repRow}>
              <span style={repAvatar}>{initials(name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>{name}</div>
                <div style={{ fontSize: 11.5, color: '#64748b' }}>{pick(r, 'representativeTextId', 'RepresentativeTextId') || ''}</div>
              </div>
              {isEdit && (
                <button style={{ ...repRemoveBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => removeRep(id)} title="Kaldır">×</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const card = {
  background: 'var(--bg-card,#fff)', border: '1px solid var(--border-color,#e5e7eb)',
  borderRadius: 18, padding: 22, boxShadow: 'var(--shadow-card, 0 12px 28px rgba(15,23,42,0.06))',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6b7280)', marginBottom: 6 };
const fieldWrap = { marginBottom: 16 };

const repCard = {
  borderRadius: 18, padding: 20,
  background: 'linear-gradient(135deg, #6d5dfc 0%, #7a3cff 55%, #a13dff 100%)',
  boxShadow: '0 16px 34px rgba(122,60,255,0.28)',
};
const repIcon = {
  width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
};
const repAddBtn = {
  border: '1px solid rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.16)', color: '#fff',
  padding: '7px 14px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 13,
};
const repRow = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
  background: 'rgba(255,255,255,0.95)', borderRadius: 12,
};
const repAvatar = {
  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
  background: 'linear-gradient(135deg,var(--accent),#7a3cff)', color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13,
};
const repRemoveBtn = {
  border: '1px solid #e5e7eb', background: '#fff', color: 'var(--danger)',
  width: 26, height: 26, borderRadius: 8, cursor: 'pointer', fontSize: 16, lineHeight: 1, fontWeight: 700,
};
